import { generatePath } from 'react-router';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import { FormField, FormFieldType } from 'types';
import {
  providesList,
  invalidatesList,
  cacheByIdArgProperty,
  cacheListAndByIdArgProperty,
} from 'utils/query-cache';

import { apiQuery } from '../../query';
import { invalidateCourseStepCaches } from '../course-steps/invalidation-helpers';
import { invalidateHealthDepartmentCaches } from '../project/invalidation-helpers';

import {
  Form,
  FormResponse,
  FormListRequest,
  FormListResponse,
  FormCreateRequest,
  FormUpdateRequest,
  FormReferenceItem,
  PaginationResponse,
  FormDuplicateRequest,
  FormReferenceItemsRequest,
  FormReferenceItemsResponse,
} from './types';

const tagType = 'FORMS';
const csvFileFieldPrefix = 'csvLookupFiles.';

const extractCsvLookupFiles = (
  fields: FormField[],
  files: Array<{ file: File; fieldId: string }>
): FormField[] =>
  fields.map((field): FormField => {
    if (field.type === FormFieldType.CSV_LOOKUP) {
      const { sourceFile, ...options } = field.options;

      if (sourceFile) {
        files.push({
          file: sourceFile,
          fieldId: field.id,
        });
      }

      return {
        ...field,
        options,
      };
    }

    if (field.type === FormFieldType.FIELD_ARRAY && field.options) {
      return {
        ...field,
        options: {
          ...field.options,
          fields: extractCsvLookupFiles(field.options.fields, files),
        },
      };
    }

    if (field.type === FormFieldType.OUTCOME_FIELDS && field.options) {
      return {
        ...field,
        options: {
          ...field.options,
          fields: extractCsvLookupFiles(field.options.fields, files),
        },
      };
    }

    return field;
  });

export const buildFormCreateRequest = (request: FormCreateRequest) => {
  const files: Array<{ file: File; fieldId: string }> = [];
  const payload: FormCreateRequest = {
    ...request,
    data: {
      ...request.data,
      blocks: request.data.blocks.map((block) => ({
        ...block,
        fields: extractCsvLookupFiles(block.fields, files),
      })),
    },
  };

  if (!files.length) {
    return {
      data: payload,
      url: URL.FORM_LIST,
    };
  }

  const formData = new FormData();
  formData.append('payload', JSON.stringify(payload));

  for (const { file, fieldId } of files) {
    formData.append(`${csvFileFieldPrefix}${fieldId}`, file);
  }

  return {
    data: formData,
    url: `${URL.FORM_LIST}/with-files`,
  };
};

export const formsApi = createApi({
  tagTypes: [tagType],
  baseQuery: apiQuery,
  reducerPath: 'formsApi',
  endpoints: (build) => ({
    details: build.query<Form, { id: string }>({
      providesTags: cacheByIdArgProperty(tagType),
      transformResponse: (response: FormResponse) => response.data,
      query: ({ id }) => ({
        method: 'get',
        url: URL.FORM_DETAILS.replace(':id', id),
      }),
    }),

    list: build.query<PaginationResponse<Form>, FormListRequest>({
      providesTags: providesList(tagType),
      transformResponse: (response: FormListResponse) => response.data,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.FORM_LIST, params),
      }),
    }),

    duplicate: build.mutation<Form, FormDuplicateRequest>({
      transformResponse: (response: FormResponse) => response.data,
      onQueryStarted: (_arg, api) => invalidateHealthDepartmentCaches(api),
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: generatePath(URL.FORM_DUPLICATE, { id }),
      }),
    }),

    update: build.mutation<Form, FormUpdateRequest>({
      invalidatesTags: cacheListAndByIdArgProperty(tagType),
      transformResponse: (response: FormResponse) => response.data,
      onQueryStarted: (_arg, api) => invalidateHealthDepartmentCaches(api),
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: URL.FORM_DETAILS.replace(':id', id),
      }),
    }),

    referenceItems: build.query<FormReferenceItem[], FormReferenceItemsRequest>(
      {
        transformResponse: (response: FormReferenceItemsResponse) =>
          response.data,
        query: ({ formId, fieldId, ...params }) => ({
          method: 'get',
          url: addParamsToUrl(
            generatePath(URL.FORM_REFERENCE_ITEMS, { formId, fieldId }),
            params
          ),
        }),
      }
    ),

    create: build.mutation<Form, FormCreateRequest>({
      invalidatesTags: invalidatesList(tagType),
      transformResponse: (response: FormResponse) => response.data,
      query: (data) => ({
        ...buildFormCreateRequest(data),
        method: 'post',
      }),
      onQueryStarted: async ({ courseStepId }, api) => {
        await Promise.all([
          invalidateCourseStepCaches(courseStepId, api),
          invalidateHealthDepartmentCaches(api),
        ]);
      },
    }),
  }),
});

export const {
  useListQuery,
  useDetailsQuery,
  useLazyListQuery,
  useCreateMutation,
  useUpdateMutation,
  useLazyDetailsQuery,
  useDuplicateMutation,
  useLazyReferenceItemsQuery,
} = formsApi;

export type { Form, FormReferenceItem };
